<script setup>
import { Inertia } from '@inertiajs/inertia'
import { PencilAltIcon } from "@heroicons/vue/outline"
import { ref, computed, onMounted } from 'vue'
import { viewInvoiceInXero } from '@/Mixins/RoutesUserToXero'

import UploadsSectionCard from '@/Components/Uploads/UploadsSectionCard.vue';
import OrganisationListInvoicesCard from '@/Components/Models/Organisation/OrganisationListInvoicesCard.vue';
import RelatedUsersCard from '@/Components/Models/User/RelatedUsersCard.vue';
import ManageCardsSectionCard from '@/Components/Payments/ManageCardsSectionCard.vue'
import OrganisationDashboard from '@/Components/Dashboard/OrganisationDashboard'
import EditNotesCard from '@/Components/EditNotesCard.vue';
import RequestCancellation from '@/Components/Models/Organisation/RequestCancellation.vue'
import XeroLogo from "@/images/XeroLogoSVG.vue"
import { ExclamationIcon } from '@heroicons/vue/solid'
import { CheckCircleIcon } from '@heroicons/vue/solid'

import { useAuthStore } from '@/Stores/auth'
const auth = useAuthStore()
import { useErrorsStore } from '@/Stores/errors'
const errors = useErrorsStore()

// since we are viewing an organisation, set the current cardable in payments to the organisation
import { usePaymentsStore } from '@/Stores/payments'
const payments = usePaymentsStore()
payments.setCardable('organisation', props.organisation.id)
payments.fetchSavedCards()

const props = defineProps({
	organisation: {
		type: Object, // an inertia form helper
		default: () => ({}),
	},
})

const emit = defineEmits([
	'update:organisations',
	'refresh:organisations',
])

// the d_ is for display
const d_membership_level = props.organisation.membership_level
const invoices = ref(props.organisation.invoices)
// we don't want the invoices list component to load its own invoices so we'll tell it we've already fetched them
const hasFetchedInvoices = true

const hasXeroContact = computed(() => {
	if (props.organisation.xero_contact_id) {
		return true
	} else {
		return false
	}
})

function editOrganisation(){
	Inertia.visit(route('dashboard.organisations.edit', props.organisation.id))
}

function save(data) {
	emit('update:organisations', data ?? props.organisation)
}

// notes card
const notes = ref(props.organisation.notes)
function updateNotes(){
	save({
		notes: notes.value
	})
}

function viewInvoice(invoice){
	viewInvoiceInXero(invoice.InvoiceID)
}

function viewUser(user){
	Inertia.visit(route('dashboard.users.show', user.id))
}

function createUser(){
	Inertia.visit(route('dashboard.users.create', {organisation_id: props.organisation.id}))
}

function invoicesLoaded(loadedInvoices){
	invoices.value = loadedInvoices
}

function fetchInvoices() {
    return axios.get('/api/invoices/organisation/' + props.organisation.id)
    .then(response => {
        invoicesLoaded(response.data)
    })
    .catch(error => {
		console.log(error);
		errors.push({
			type: 'error',
			title: 'Error fetching invoices',
            message: error.response.data.message,
        })
    })
}

onMounted(() => {
	fetchInvoices()
})
</script>

<template>
	<SectionCard data-testid="view-organisation">
		<template #header>
			<div class="flex flex-col md:flex-row w-full md:justify-between md:items-center">
				<div>
					<h1 class="text-2xl font-bold">{{ organisation.name }}</h1>
				</div>
				<p class="text-sm">Member since: {{ organisation.joined_date }}</p>
			</div>
			<p class="font-medium mt-4 md:mt-0 capitalize">{{ d_membership_level }} member</p>
		</template>
		<template #default>
			<div class="md:grid md:grid-cols-2">
				<div class="md:col-span-1">
					<div class="mb-3">
						<Label>ABN</Label>
						<p>{{ organisation.abn }}</p>
					</div>
					<div class="mb-3">
						<Label>Address</Label>
						<p>{{ organisation.street_address }}</p>
						<p>{{ organisation.city }}</p>
						<p>{{ organisation.state }} {{ organisation.postal_code }}</p>
					</div>
				</div>
				
				<div class="md:col-span-1">
					<div class="mb-3">
						<Label>Website</Label>
						<p><a :href="organisation.website">{{ organisation.website }}</a></p>
					</div>
					<div class="mb-3">
						<Label>Phone</Label>
						<p><a :href="'tel:' + organisation.phone">{{ organisation.phone }}</a></p>
					</div>
					<div class="mb-3">
						<Label>Email</Label>
						<p><a :href="'mailto:' + organisation.email">{{ organisation.email }}</a></p>
					</div>
				</div>
				<div class="mb-3">
					<Label>Payment Frequency</Label>
					<p class="capitalize">{{organisation.payment_frequency}}</p>
				</div>
				<div class="mb-3">
					<Label>Membership Status</Label>
					<p>{{organisation.membership_status}}</p>
				</div>
				<div class="mb-3">
					<Label>Membership Expiry Date</Label>
					<p>{{organisation.membership_expires_at}}</p>
				</div>
				<div class="mb-3">
					<Label>Membership Fee</Label>
					<p>{{organisation.membership_fee}}</p>
				</div>
				
			</div>
			<div v-if="auth.isAdmin">
				<div v-if="organisation.xero_id" class="flex items-center mt-6">
					<XeroLogo class="h-4 w-16" />
					<CheckCircleIcon  class="h-5 w-5 text-green-500" aria-hidden="true" />
					<p class="ml-2 mb-[1px]"><a class="text-Blue text-sm font-medium no-underline" :href="'https://go.xero.com/Contacts/View/' + organisation.xero_id">View Contact in Xero</a></p>		
				</div>
				<div v-else class="flex items-center mt-6">
					<ExclamationIcon class="h-12 w-12 mr-3 text-yellow-400" aria-hidden="true" />
					<p class="text-sm font-medium">There is currently no Xero Contact ID associated with this organisation. You will not be able to sync invoices without a Xero Contact ID. Click "Edit Details" to add one now.</p>
				</div>
			</div>
		</template>
		
		<template #footer>
			<Button 
				@click="editOrganisation" 
				class="block text-center" 
				color="blue"
				v-if="auth.can('update', 'organisations', organisation.id)"
				data-testid="edit-details-btn"
			>
				<PencilAltIcon class="w-4 h-4 mr-2 inline" /> Edit Details
			</Button>
		</template>
	</SectionCard>

	<RelatedUsersCard 
		v-if="auth.isAdmin"
		:users="organisation.users"
		:relationship="'organisations'"
		:related="organisation"
		@create:users="createUser"
		@clicked="viewUser"
	/>

	<OrganisationDashboard v-if="auth.isAdmin" :organisation="organisation" :user-stats="{}" />

	<OrganisationListInvoicesCard 
		v-if="auth.isAdmin"
		class="my-3" 
		data-testid="organisation-invoices" 
		:organisation="organisation" 
		:invoices="invoices" 
		@invoices-loaded="invoicesLoaded" 
		@item-clicked="viewInvoice"
		:has-fetched-invoices="hasFetchedInvoices"
	/>

	<ManageCardsSectionCard
		v-if="auth.isAdmin"
		:organisation="organisation"
	/>

	<EditNotesCard 
		v-if="auth.isAdmin"
		v-model="notes"
		data-testid="organisation-notes"
		@update:modelValue="updateNotes"
	/>

	<UploadsSectionCard
		v-if="auth.isAdmin"
		:uploads="uploads"
		:files="organisation.files"
		fileable_type="organisation"
		:fileable_id="organisation.id"
		@upload-complete="emit('refresh:organisations')"
		@file-deleted="emit('refresh:organisations')"
	/>

	<RequestCancellation
		v-if="auth.can('request_cancellation', 'organisations', organisation.id)"
		:organisation="organisation"
	/>
</template>